/**
 * URL Parser Module
 *
 * This module handles parsing and encoding URLs in the format:
 * /page1:param1~value1:param2~value2/page2:param1~value/page3
 *
 * This is a pure text parsing module with no dependencies on Angular services
 * or app-specific logic. It only handles the URL format conversion.
 */

export interface PageWithParams {
  segment: string;  // The page segment name (e.g., 'home', 'weigh-in', 'testing')
  params: Record<string, string>;  // Parameters as key-value pairs
}

/**
 * Parse a URL string into an array of pages with their parameters.
 *
 * @param url The URL to parse (leading slash and query params are handled)
 * @returns Array of pages with their parameters
 */
export function parseUrlToPages(url: string): PageWithParams[] {
  // Remove leading slash if present
  if (url.charAt(0) === '/') {
    url = url.substring(1);
  }

  // Remove query parameters and hash
  const pathPart = url.split('?')[0].split('#')[0];

  // If empty, return empty array
  if (!pathPart) {
    return [];
  }

  // Split into page segments
  const segments = pathPart.split('/');
  const pages: PageWithParams[] = [];

  for (const segment of segments) {
    if (!segment.trim()) continue;

    const page = parsePageSegment(segment);
    if (page) {
      pages.push(page);
    }
  }

  return pages;
}

/**
 * Parse a single page segment.
 * Format: pageName:param1~value1:param2~value2 or just pageName
 */
function parsePageSegment(segment: string): PageWithParams | null {
  // Split page name from parameters
  const [pageName, ...paramParts] = segment.split(':');

  if (!pageName) {
    return null;
  }

  const params: Record<string, string> = {};

  // Parse parameters if present
  for (const paramPart of paramParts) {
    const [paramName, encodedValue] = paramPart.split('~');
    if (paramName && encodedValue !== undefined) {
      params[paramName] = decodeParameterValue(encodedValue);
    }
  }

  return {
    segment: pageName,
    params
  };
}

/**
 * Generate a URL string from an array of pages with their parameters.
 *
 * @param pages Array of pages with their parameters
 * @returns URL string with leading slash
 */
export function pagesToUrl(pages: PageWithParams[]): string {
  if (!pages || pages.length === 0) {
    return '/';
  }

  const segments = pages.map(page => encodePageSegment(page));
  return '/' + segments.join('/');
}

/**
 * Encode a single page with its parameters into a segment string.
 * Format: pageName:param1~value1:param2~value2
 */
function encodePageSegment(page: PageWithParams): string {
  if (!page.params || Object.keys(page.params).length === 0) {
    return page.segment;
  }

  const paramPairs: string[] = [];

  for (const [paramName, value] of Object.entries(page.params)) {
    const encodedValue = encodeParameterValue(value);
    paramPairs.push(`${paramName}~${encodedValue}`);
  }

  return `${page.segment}:${paramPairs.join(':')}`;
}

/**
 * Encode a parameter value, handling special characters.
 */
function encodeParameterValue(value: string): string {
  if (value === '') {
    return '_'; // Use underscore for empty values
  }

  // Apply standard URL encoding first
  let encoded = encodeURIComponent(value);

  // Then escape our separator ~ (which wasn't encoded by encodeURIComponent)
  // This prevents conflicts with our ~ delimiter
  encoded = encoded.replace(/~/g, '%7E');

  return encoded;
}

/**
 * Decode a parameter value, reversing the encoding.
 */
function decodeParameterValue(encodedValue: string): string {
  if (encodedValue === '_') {
    return ''; // Underscore represents empty values
  }

  try {
    // Apply standard URL decoding
    let decoded = decodeURIComponent(encodedValue);

    // Restore ~ that was escaped
    decoded = decoded.replace(/%7E/g, '~');

    return decoded;
  } catch (error) {
    console.warn('url-parser: Failed to decode parameter value:', encodedValue, error);
    return encodedValue; // Return as-is if decoding fails
  }
}